/**
 * The IronKeep-issued /set-password link.
 *
 * Per decision 9 in
 * .superpowers/sdd/2026-09-26-ironkeep-user-verbs/task-2-brief.md: IronKeep
 * hands customers `https://ledger.ironmesh.cloud/set-password?token=<JWT>`.
 * It cannot put the token in a FRAGMENT the way /accept-invite and /reset do
 * (there is no browser on IronKeep's side to choose one), so this one arrives
 * in the query string instead — which means it reaches the server in a
 * request line and would show up in a Referer header on whatever the page
 * loads next. App.jsx strips it with `history.replaceState` as the very first
 * thing it does with the route this returns; that half is not pure and does
 * not belong here.
 *
 * The JWT is one of the two shapes api/src/lib/tokens.js's callers sign:
 * `{tenantId, userId, secret, purpose:'reset'}` or
 * `{tenantId, invitationId, secret, purpose:'invite'}`. Decoding the payload
 * here is for CHOOSING A MODE ONLY — the server verifies the signature on
 * submit, so a forged or expired token still reaches `/api/users/accept-invite`
 * or `/api/users/reset` and fails there, same as today.
 *
 * IN `lib/` FOR THE SAME REASON routing.js and views.js are: `npm test -w
 * web` is node's own runner over `src/lib/*.test.js`, and node cannot parse
 * JSX. A route reader only a component could exercise is one nobody checks.
 */

const PURPOSE_TO_MODE = { reset: 'reset', invite: 'invite' };

/**
 * The middle segment of a JWT, base64url-decoded and JSON-parsed — or null
 * for anything that is not a well-formed three-segment token with a JSON
 * object in the middle. Never throws.
 */
function decodeJwtPayload(token) {
  const parts = String(token ?? '').split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    // jsonwebtoken (the signer, api/src/lib/auth.js and ironkeepUsers.js)
    // emits base64url with the padding stripped, so it has to be restored
    // before atob — a segment whose length isn't a multiple of 4 otherwise
    // throws rather than mis-decoding.
    const b64url = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64url + '='.repeat((4 - (b64url.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

/**
 * Read the /set-password route: the token from `?token=`, falling back to the
 * fragment (raw token, same convention as /accept-invite and /reset), and the
 * mode from the decoded payload's `purpose`.
 *
 * Returns `{ mode: 'invite' | 'reset', token }` when the link is usable, or
 * `{ mode: null }` for anything else — no token, an unparseable JWT, or a
 * `purpose` this app does not know. The caller renders one "this link is not
 * valid" state for every `mode: null`; this module does not distinguish why.
 */
export function readSetPasswordRoute(search, hash) {
  const fromQuery = new URLSearchParams(search ?? '').get('token');
  const token = fromQuery || String(hash ?? '').replace(/^#/, '');
  if (!token) return { mode: null };

  const payload = decodeJwtPayload(token);
  // Object.hasOwn, NOT `PURPOSE_TO_MODE[payload.purpose]` — a plain object
  // reaches Object.prototype, so a payload minted (or forged) with
  // `purpose: 'constructor'` or `'toString'` would otherwise find an
  // inherited function, which is truthy, and route into SetPassword instead
  // of the invalid-link state. Same guard, and same reason, as canView in
  // views.js. `typeof === 'string'` first because Object.hasOwn coerces its
  // key argument to a string, so a non-string purpose (1, null, {}) could
  // otherwise collide with a real key by accident of coercion.
  const mode = payload && typeof payload.purpose === 'string'
    && Object.hasOwn(PURPOSE_TO_MODE, payload.purpose)
    ? PURPOSE_TO_MODE[payload.purpose]
    : undefined;
  return mode ? { mode, token } : { mode: null };
}
